/* eslint-disable @typescript-eslint/no-unused-vars */
import { useEffect, useState, ReactElement, useRef, useContext } from "react";
import axios from "axios";
import Card from "components/card";
import { Button, Badge } from "@chakra-ui/react";
import { FaAngleDown, FaAngleLeft } from "react-icons/fa";
import SweetAlert2 from "react-sweetalert2";
import BatchPayoutList from "./components/BatchPayoutList";
import BatchDetailErrorInfoModal from "./components/BatchDetailErrorInfoModal";
import {
  MdOutlineCancel,
  MdOutlineCheckCircleOutline,
  MdOutlinePlayCircleOutline,
  MdPersonAddAlt,
} from "react-icons/md";
import { ImCogs } from "react-icons/im";
import { useParams, useNavigate } from "react-router-dom";
import { HiDotsVertical } from "react-icons/hi";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import customParseFormat from "dayjs/plugin/customParseFormat";
import BatchCancelConfirmModal from "./components/BatchCancelConfirmModal";
import BatchUpdateStatusConfirmModal from "./components/BatchUpdateStatusConfirmModal";
import BatchAssignProviderModal from "./components/BatchAssignProviderModal";
import BatchProcessConfirmModal from "./components/BatchProcessConfirmModal";
import { AiOutlineDownload } from "react-icons/ai";
import { ProfileContext } from "../../../store/ProfileProvider";
import DisplayAmount from "../shared/DisplayAmount";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);

const rowPerPage = 10;

interface PayloadType {
  [key: string]: any;
}

const BatchDetail = ({ userPermissions }: { userPermissions: any }) => {
  const { profileTimeZone } = useContext(ProfileContext);
  const [batchDetailForEdit, setBatchDetailForEdit] = useState(null);
  const [batchProviderList, setBatchProviderList] = useState(null);
  const [swalProps, setSwalProps] = useState<any>({});
  const [tableData, setTableData] = useState([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isFetchProvider, setIsFetchProvider] = useState<boolean>(true);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const [lastKeys, setLastKeys] = useState<{ [key: number]: string }>({});
  const [currentLastKey, setCurrentLastKey] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(rowPerPage);
  const [rowCount, setRowCount] = useState(rowPerPage);
  const [isOpenBatchDetailErrorInfoModal, setIsOpenBatchDetailErrorInfoModal] =
    useState<boolean>(false);

  const [batchToCancel, setBatchToCancel] = useState<any>(null);
  const [isOpenBatchCancelConfirmModal, setIsOpenBatchCancelConfirmModal] =
    useState<boolean>(false);

  const [batchToUpdateStatus, setBatchToUpdateStatus] = useState<any>(null);
  const [
    isOpenBatchUpdateStatusConfirmModal,
    setIsOpenBatchUpdateStatusConfirmModal,
  ] = useState<boolean>(false);

  const [batchToAssignProvider, setBatchToAssignProvider] = useState<any>(null);
  const [isOpenBatchAssignProviderModal, setIsOpenBatchAssignProviderModal] =
    useState<boolean>(false);

  const [batchToProcessConfirm, setBatchToProcessConfirm] = useState<any>(null);
  const [isOpenBatchProcessConfirmModal, setIsOpenBatchProcessConfirmModal] =
    useState<boolean>(false);

  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
  const [statusToUpdate, setStatusToUpdate] = useState<string>("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { id } = useParams();
  const userRole = localStorage.getItem("userRole")?.toLowerCase();

  useEffect(() => {
    const fetchData = async () => {
      await fetchBatchDetail(id);
    };

    if (batchDetailForEdit === null) {
      fetchData();
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []); // eslint-disable-line

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node)
    ) {
      setIsDropdownOpen(false);
    }
  };

  const fetchBatchDetail = async (batchId: string) => {
    try {
      setBatchDetailForEdit(null);
      setIsLoading(true);

      const response = await axios.get(
        process.env.REACT_APP_API_URL +
          `/api/batch/func/get-batch-by-id?batchid=${batchId}`
      );

      let batchData = response.data.batch?.payload;

      // Check if batch data exists, if not redirect to batch listing
      if (!batchData) {
        console.error("Batch not found");
        navigate(`/${userRole}/batch`);
        return;
      }

      setBatchDetailForEdit(batchData);
      await fetchPayouts({
        page: 0,
        limit: rowPerPage,
        lastkey: "",
        batch_id: id,
      });
      await fetchBatchProvider(String(id));
    } catch (error) {
      setTableData([]);
      setIsLoading(false);
      console.error("Error fetching data", error);
      // Redirect to batch listing page on error
      navigate(`/${userRole}/batch`);
    }
  };

  const fetchBatchProvider = async (id: string) => {
    try {
      const response = await axios.get(
        process.env.REACT_APP_API_URL +
          `/api/batch/func/get-method?type=batch&entityid=${id}`
      );

      let providerData = response.data.provider;

      setBatchProviderList(providerData);
      setIsFetchProvider(false);
    } catch (error) {
      console.error("Error fetching data", error);
    }
  };

  const fetchPayouts = async (payload: PayloadType = {}) => {
    setIsLoading(true);
    try {
      const queryObject: PayloadType = {
        limit: payload?.limit,
        lastkey: payload?.lastkey,
        batch_id: payload?.batch_id,
        usertype: userRole === "superadmin" ? "a" : "m",
      };

      const queryString = Object.keys(queryObject)
        .map(
          (key) =>
            `${encodeURIComponent(key)}=${encodeURIComponent(queryObject[key])}`
        )
        .join("&");

      const response = await axios.get(
        process.env.REACT_APP_API_URL +
          `/api/payout/get-all-payouts?${queryString}`
      );

      let newData = response.data.payouts?.payload;

      if (newData.length !== 0) {
        setTableData(newData);
      } else {
        setTableData([]);
      }
      setIsLoading(false);

      let newLastKey = response.data.payouts?.lastkey;
      if (newLastKey) {
        setCurrentLastKey(newLastKey);
        let newPage = payload?.page;
        let newPageSize = payload?.limit;

        if (newData.length !== 0) {
          setRowCount((newPage + 1) * newPageSize + newPageSize);
        } else {
          setRowCount(newPage * newPageSize + newPageSize);
        }
        setLastKeys({ ...lastKeys, [newPage]: newLastKey });
      } else {
        setCurrentLastKey("");
      }
    } catch (error) {
      setTableData([]);
      setIsLoading(false);
      setPage(0);
      setPageSize(0);
      setRowCount(0);
      setLastKeys([]);
      setCurrentLastKey("");
      console.error("Error fetching data", error);
    }
  };

  const setPaginationModel = (newValue: any) => {
    setIsLoading(true);

    let newFilters: any = {
      page: newValue.page,
      limit: newValue.pageSize,
      lastkey: currentLastKey,
      batch_id: id,
    };

    if (newValue.page < page) {
      newFilters = {
        ...newFilters,
        lastkey: newValue.page !== 0 ? lastKeys[newValue.page - 1] : "",
      };
    }

    if (newValue.pageSize !== pageSize) {
      newFilters = {
        page: 0,
        limit: newValue.pageSize,
        lastkey: "",
        batch_id: id,
      };
      setPage(0);
      setPageSize(newValue.pageSize);
      setCurrentLastKey("");
      setLastKeys([]);
    } else {
      setPage(newValue.page);
    }

    fetchPayouts(newFilters);
  };

  interface BatchStatusItems {
    status: string;
    value: string;
    display: ReactElement;
  }

  const statusClasses = "status";

  const statusList: BatchStatusItems[] = [
    {
      status: "open",
      value: "open",
      display: <Badge className={`${statusClasses} status-open`}>Open</Badge>,
    },
    {
      status: "close",
      value: "close",
      display: (
        <Badge className={`${statusClasses} status-closed`}>Closed</Badge>
      ),
    },
    {
      status: "queue",
      value: "queue",
      display: (
        <Badge className={`${statusClasses} status-queued`}>Queued</Badge>
      ),
    },
    {
      status: "pending",
      value: "pending",
      display: (
        <Badge className={`${statusClasses} status-pending`}>Pending</Badge>
      ),
    },
    {
      status: "in_progress",
      value: "in_progress",
      display: (
        <Badge className={`${statusClasses} status-in_progress`}>
          In Progress
        </Badge>
      ),
    },
    {
      status: "settled",
      value: "settled",
      display: (
        <Badge className={`${statusClasses} status-settled`}>Settled</Badge>
      ),
    },
    {
      status: "settled_with_error",
      value: "settled_with_error",
      display: (
        <Badge className={`${statusClasses} status-settled_with_error`}>
          Settled with Errors
        </Badge>
      ),
    },
    {
      status: "cancelled",
      value: "cancelled",
      display: (
        <Badge className={`${statusClasses} status-cancelled`}>Cancelled</Badge>
      ),
    },
    {
      status: "failed",
      value: "failed",
      display: (
        <Badge className={`${statusClasses} status-failed`}>Failed</Badge>
      ),
    },
  ];

  const statusDisplay = (status: string) => {
    const statusItem = statusList.find((item) => item.status === status);
    return statusItem ? statusItem.display : null;
  };

  const formatDate = (timestamp: string, time: boolean = false) => {
    if (timestamp && timestamp !== "") {
      if (time) {
        return dayjs(Number(timestamp))
          .tz(profileTimeZone)
          .format("DD MMM YY - hh:mm A");
      } else {
        return dayjs(Number(timestamp)).tz(profileTimeZone).format("DD MMM YY");
      }
    }
    return "-";
  };

  const showErrorInfoModal = () => {
    setIsOpenBatchDetailErrorInfoModal(true);
  };

  const closeErrorInfoModal = () => {
    setIsOpenBatchDetailErrorInfoModal(false);
  };

  const handleClickBack = () => {
    navigate(`/${userRole}/batch`);
  };

  const handleClickCancel = () => {
    setBatchToCancel(batchDetailForEdit);
    showBatchCancelConfirmModal();
  };

  const showBatchCancelConfirmModal = () => {
    setIsOpenBatchCancelConfirmModal(true);
  };

  const closeBatchCancelConfirmModal = () => {
    setBatchToCancel(null);
    setIsOpenBatchCancelConfirmModal(false);
  };

  const handleClickUpdateStatus = (status: string) => {
    setIsDropdownOpen(false);
    setStatusToUpdate(status);
    setBatchToUpdateStatus(batchDetailForEdit);
    showBatchUpdateStatusConfirmModal();
  };

  const showBatchUpdateStatusConfirmModal = () => {
    setIsOpenBatchUpdateStatusConfirmModal(true);
  };

  const closeBatchUpdateStatusConfirmModal = () => {
    setBatchToUpdateStatus(null);
    setIsOpenBatchUpdateStatusConfirmModal(false);
  };

  const handleClickAssignProvider = () => {
    setBatchToAssignProvider(batchDetailForEdit);
    showBatchAssignProviderModal();
  };

  const showBatchAssignProviderModal = () => {
    setIsOpenBatchAssignProviderModal(true);
  };

  const closeBatchAssignProviderModal = () => {
    setBatchToAssignProvider(null);
    setIsOpenBatchAssignProviderModal(false);
  };

  const handleClickProcessConfirm = () => {
    setBatchToProcessConfirm(batchDetailForEdit);
    showBatchProcessConfirmModal();
  };

  const showBatchProcessConfirmModal = () => {
    setIsOpenBatchProcessConfirmModal(true);
  };

  const closeBatchProcessConfirmModal = () => {
    setBatchToProcessConfirm(null);
    setIsOpenBatchProcessConfirmModal(false);
  };

  const triggerReloadBatchDetail = async () => {
    setIsLoading(true);
    await fetchBatchDetail(id);
  };

  const refreshBatchDetailSilent = async () => {
    try {
      const response = await axios.get(
        process.env.REACT_APP_API_URL +
          `/api/batch/func/get-batch-by-id?batchid=${id}`
      );
      let batchData = response.data.batch?.payload;
      if (batchData) {
        setBatchDetailForEdit(batchData);
      }
    } catch (error) {
      console.error("Error refreshing batch detail", error);
    }
  };

  const triggerReloadPayoutList = async () => {
    setIsLoading(true);
    await refreshBatchDetailSilent();
    await fetchPayouts({
      page: 0,
      limit: rowPerPage,
      lastkey: "",
      batch_id: batchDetailForEdit.id,
    });
  };

  const handleClickDownload = async (
    file_bucket: string,
    file_key: string,
    file_name: string
  ) => {
    try {
      setIsDownloading(true);

      const postData = {
        file_bucket: file_bucket,
        file_key: file_key,
      };

      await axios
        .post(
          process.env.REACT_APP_API_URL +
            `/api/${userRole}/batch/action/batch-generate-download-url`,
          postData
        )
        .then(async function (response) {
          setIsDownloading(false);
          if (response && response.data?.status === "ok") {
            const fileUrl = response.data.url;
            const fileResponse = await fetch(fileUrl);
            const blob = await fileResponse.blob();

            const link = document.createElement("a");
            link.href = window.URL.createObjectURL(blob);
            link.download = file_name;
            link.click();

            window.URL.revokeObjectURL(link.href);
          } else {
            setSwalProps({
              show: true,
              icon: "error",
              title: "Oops!",
              html: response.data.message,
              showConfirmButton: true,
              didClose: () => {
                setSwalProps({});
              },
            });
          }
        })
        .catch(function (error) {
          setSwalProps({
            show: true,
            icon: "error",
            title: "Oops!",
            html: error.response.data.message,
            showConfirmButton: true,
            didClose: () => {
              setSwalProps({});
            },
          });
        });
    } catch (error) {
      setSwalProps({
        show: true,
        icon: "error",
        title: "Oops!",
        html: "Something went wrong, please try again later.",
        showConfirmButton: true,
        didClose: () => {
          setSwalProps({});
        },
      });
    }
  };

  return (
    <>
      {isOpenBatchCancelConfirmModal && (
        <BatchCancelConfirmModal
          setSwalProps={setSwalProps}
          triggerReload={triggerReloadBatchDetail}
          isOpen={isOpenBatchCancelConfirmModal}
          batchToCancel={batchToCancel}
          onClose={closeBatchCancelConfirmModal}
        />
      )}

      {userRole === "superadmin" && (
        <>
          {isOpenBatchAssignProviderModal && (
            <BatchAssignProviderModal
              setSwalProps={setSwalProps}
              triggerReload={triggerReloadBatchDetail}
              isOpen={isOpenBatchAssignProviderModal}
              batchToAssignProvider={batchToAssignProvider}
              batchProviderList={batchProviderList}
              onClose={closeBatchAssignProviderModal}
            />
          )}
          {isOpenBatchProcessConfirmModal && (
            <BatchProcessConfirmModal
              setSwalProps={setSwalProps}
              triggerReload={triggerReloadBatchDetail}
              isOpen={isOpenBatchProcessConfirmModal}
              batchToProcessConfirm={batchToProcessConfirm}
              onClose={closeBatchProcessConfirmModal}
            />
          )}
          {isOpenBatchUpdateStatusConfirmModal && (
            <BatchUpdateStatusConfirmModal
              setSwalProps={setSwalProps}
              statusToUpdate={statusToUpdate}
              triggerReload={triggerReloadBatchDetail}
              isOpen={isOpenBatchUpdateStatusConfirmModal}
              batchToUpdateStatus={batchToUpdateStatus}
              onClose={closeBatchUpdateStatusConfirmModal}
            />
          )}
        </>
      )}

      {isOpenBatchDetailErrorInfoModal &&
        batchDetailForEdit !== null &&
        batchDetailForEdit.validation_results && (
          <BatchDetailErrorInfoModal
            data={batchDetailForEdit.validation_results}
            isOpen={isOpenBatchDetailErrorInfoModal}
            onClose={closeErrorInfoModal}
          />
        )}

      <Card className="card card-pad !rounded-xl">
        {batchDetailForEdit !== null ? (
          <>
            <div className="mb-3 flex items-center">
              <div>
                <button
                  type="button"
                  onClick={handleClickBack}
                  disabled={isLoading || isDownloading}
                  className="
                    mb-1 inline-flex items-center gap-1
                    text-sm font-medium text-gray-700
                    transition hover:text-gray-700
                    disabled:cursor-not-allowed disabled:opacity-40
                    dark:text-gray-400 dark:hover:text-white
                  "
                >
                  <FaAngleLeft className="text-base" />
                  <span>Batches</span>
                </button>

                <h3 className="text-xl font-semibold text-navy-700">
                  Batch{" "}
                  <span className="font-semibold">
                    #{batchDetailForEdit?.id || "-"}
                  </span>
                </h3>
              </div>

              <div className="ml-auto flex items-center">
                {/* old code */}
                {/* <div className="flex">
                  {batchDetailForEdit.status === "open" &&
                    userRole !== "superadmin" &&
                    userPermissions?.cancelBatch && (
                      <>
                        <span className="mx-3 inline-block h-8 border-l border-gray-200"></span>
                        <Button
                          isDisabled={batchDetailForEdit === null || isLoading}
                          onClick={() => handleClickCancel()}
                          className="btn rounded-xl bg-red-600 text-sm font-medium text-white transition duration-200 hover:bg-red-500 hover:text-white active:bg-red-700 dark:border-red-400 dark:text-red-400 dark:active:bg-red-200"
                        >
                          Cancel Batch
                        </Button>
                      </>
                    )}
                  {batchDetailForEdit.status === "in_progress" &&
                    batchDetailForEdit.is_manual === "false" &&
                    batchDetailForEdit.is_can_process_again === "true" &&
                    userRole === "superadmin" && (
                      <>
                        <span className="mx-3 inline-block h-8 border-l border-gray-200"></span>
                        <Button
                          isDisabled={batchDetailForEdit === null || isLoading}
                          onClick={() => handleClickProcessConfirm()}
                          className="btn rounded-xl border border-amber-500 text-sm font-medium text-amber-500 transition duration-200 hover:bg-amber-500 hover:text-white active:bg-amber-700 dark:border-amber-400 dark:text-amber-400 dark:active:bg-amber-200"
                        >
                          <MdOutlinePlayCircleOutline className="mr-2 text-base" />
                          Process
                        </Button>
                      </>
                    )}
                  {(["open", "close", "queue"].includes(
                    batchDetailForEdit.status
                  ) ||
                    (batchDetailForEdit.status === "in_progress" &&
                      batchDetailForEdit.is_manual === "true")) &&
                    userRole === "superadmin" && (
                      <>
                        <span className="mx-3 inline-block h-8 border-l border-gray-200"></span>
                        <div ref={dropdownRef}>
                          <Button
                            onClick={toggleDropdown}
                            isDisabled={
                              batchDetailForEdit === null ||
                              isLoading ||
                              isDownloading ||
                              isFetchProvider
                            }
                            className="btn-primary relative overflow-hidden rounded-md px-6 py-2.5 text-sm font-medium text-white shadow-md transition-colors duration-300 hover:bg-primary-500 active:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
                          >
                            {(batchDetailForEdit === null ||
                              isLoading ||
                              isFetchProvider) && <>Preparing...</>}
                            {batchDetailForEdit &&
                              !isLoading &&
                              !isFetchProvider && (
                                <>
                                  Action&nbsp;
                                  <FaAngleDown />
                                </>
                              )}
                          </Button>
                          {isDropdownOpen && (
                            <div className="absolute mt-2 origin-top-right rounded-md border border-gray-300 bg-white shadow-lg">
                              <div
                                className="p-2"
                                role="menu"
                                aria-orientation="vertical"
                                aria-labelledby="options-menu"
                              >
                                {userRole === "superadmin" &&
                                  (batchDetailForEdit.status === "open" ||
                                    batchDetailForEdit.status === "close") && (
                                    <span
                                      onClick={() =>
                                        handleClickAssignProvider()
                                      }
                                      className="flex cursor-pointer items-center px-2 py-2 text-sm font-medium text-navy-500 hover:bg-navy-100"
                                    >
                                      <ImCogs className="mr-2 text-base" />
                                      Assign Provider
                                    </span>
                                  )}
                                {userRole === "superadmin" &&
                                  batchDetailForEdit.status === "close" && (
                                    <span
                                      onClick={() =>
                                        handleClickProcessConfirm()
                                      }
                                      className="flex cursor-pointer items-center px-2 py-2 text-sm font-medium text-amber-500 hover:bg-amber-100"
                                    >
                                      <MdOutlinePlayCircleOutline className="mr-2 text-base" />
                                      Process
                                    </span>
                                  )}
                                {userRole === "superadmin" &&
                                  batchDetailForEdit.status === "queue" &&
                                  batchDetailForEdit.file_bucket !== null &&
                                  batchDetailForEdit.file_key !== null && (
                                    <span
                                      onClick={() =>
                                        handleClickUpdateStatus("in_progress")
                                      }
                                      className="flex cursor-pointer items-center px-2 py-2 text-sm font-medium text-amber-500 hover:bg-amber-100"
                                    >
                                      <MdOutlineCheckCircleOutline className="mr-2 h-4 w-4 shrink-0" />
                                      Mark Batch as In Progress
                                    </span>
                                  )}
                                {(userRole === "superadmin" &&
                                  (batchDetailForEdit.status === "open" ||
                                    batchDetailForEdit.status === "close" ||
                                    batchDetailForEdit.status === "queue")) ||
                                  (userRole === "merchant" &&
                                    batchDetailForEdit.status === "open" &&
                                    userPermissions?.cancelBatch) ? (
                                    <span
                                      onClick={() => handleClickCancel()}
                                      className="flex cursor-pointer items-center px-2 py-2 text-sm font-medium text-red-500 hover:bg-red-100"
                                    >
                                      <MdOutlineCancel className="mr-2 h-4 w-4 shrink-0" />
                                      Cancel
                                    </span>
                                  ) : null}
                                {userRole === "superadmin" &&
                                  batchDetailForEdit.status === "in_progress" &&
                                  batchDetailForEdit.is_manual === "true" && (
                                    <span
                                      onClick={() =>
                                        handleClickUpdateStatus("settled")
                                      }
                                      className="flex cursor-pointer items-center px-2 py-2 text-sm font-medium text-green-500 hover:bg-green-100"
                                    >
                                      <MdOutlineCheckCircleOutline className="mr-2 h-4 w-4 shrink-0" />
                                      Mark Batch as Settled
                                    </span>
                                  )}
                                {userRole === "superadmin" &&
                                  batchDetailForEdit.status === "in_progress" &&
                                  batchDetailForEdit.is_manual === "true" && (
                                    <span
                                      onClick={() =>
                                        handleClickUpdateStatus("failed")
                                      }
                                      className="flex cursor-pointer items-center px-2 py-2 text-sm font-medium text-red-500 hover:bg-red-100"
                                    >
                                      <MdOutlineCancel className="mr-2 h-4 w-4 shrink-0" />
                                      Mark Batch as Failed
                                    </span>
                                  )}
                              </div>
                            </div>
                          )}
                        </div>
                      </>
                    )}
                </div> */}

                <div className="flex items-center">
                  {batchDetailForEdit.status === "open" &&
                    userRole !== "superadmin" &&
                    userPermissions?.cancelBatch && (
                      <>
                        <span className="mx-3 inline-block h-8 border-l border-gray-200" />
                        <Button
                          isDisabled={batchDetailForEdit === null || isLoading}
                          onClick={() => handleClickCancel()}
                          className="btn rounded-xl bg-red-600 text-sm font-medium text-white transition duration-200 hover:bg-red-500 hover:text-white active:bg-red-700"
                        >
                          Cancel Batch
                        </Button>
                      </>
                    )}

                  {batchDetailForEdit.status === "in_progress" &&
                    batchDetailForEdit.is_manual === "false" &&
                    batchDetailForEdit.is_can_process_again === "true" &&
                    userRole === "superadmin" && (
                      <>
                        <span className="mx-3 inline-block h-8 border-l border-gray-200" />
                        <Button
                          isDisabled={batchDetailForEdit === null || isLoading}
                          onClick={() => handleClickProcessConfirm()}
                          className="btn rounded-xl border border-amber-500 text-sm font-medium text-amber-500 transition duration-200 hover:bg-amber-500 hover:text-white active:bg-amber-700"
                        >
                          <MdOutlinePlayCircleOutline className="mr-2 text-base" />
                          Process
                        </Button>
                      </>
                    )}

                  {(["open", "close", "queue"].includes(
                    batchDetailForEdit.status
                  ) ||
                    (batchDetailForEdit.status === "in_progress" &&
                      batchDetailForEdit.is_manual === "true")) &&
                    userRole === "superadmin" && (
                      <>
                        {batchDetailForEdit.status === "close" ? (
                          <Button
                            isDisabled={
                              batchDetailForEdit === null ||
                              isLoading ||
                              isFetchProvider
                            }
                            onClick={() => handleClickProcessConfirm()}
                            className="btn rounded-xl border border-amber-500 text-sm font-medium text-amber-500 transition duration-200 hover:bg-amber-500 hover:text-white active:bg-amber-700"
                          >
                            <MdOutlinePlayCircleOutline className="mr-2 text-base" />
                            Process
                          </Button>
                        ) : batchDetailForEdit.status === "queue" &&
                          batchDetailForEdit.file_bucket !== null &&
                          batchDetailForEdit.file_key !== null ? (
                          <Button
                            isDisabled={
                              batchDetailForEdit === null ||
                              isLoading ||
                              isFetchProvider
                            }
                            onClick={() =>
                              handleClickUpdateStatus("in_progress")
                            }
                            className="btn rounded-xl border border-amber-500 text-sm font-medium text-amber-500 transition duration-200 hover:bg-amber-500 hover:text-white active:bg-amber-700"
                          >
                            <MdOutlineCheckCircleOutline className="mr-2 h-5 w-5 shrink-0" />
                            Mark In Progress
                          </Button>
                        ) : batchDetailForEdit.status === "in_progress" &&
                          batchDetailForEdit.is_manual === "true" ? (
                          <Button
                            isDisabled={
                              batchDetailForEdit === null ||
                              isLoading ||
                              isFetchProvider
                            }
                            onClick={() => handleClickUpdateStatus("settled")}
                            className="btn rounded-xl border border-green-500 text-sm font-medium text-green-600 transition duration-200 hover:bg-green-500 hover:text-white active:bg-green-700"
                          >
                            <MdOutlineCheckCircleOutline className="mr-2 h-5 w-5 shrink-0" />
                            Mark Settled
                          </Button>
                        ) : (
                          <Button
                            isDisabled={
                              batchDetailForEdit === null ||
                              isLoading ||
                              isDownloading ||
                              isFetchProvider
                            }
                            onClick={() => handleClickAssignProvider()}
                            className="btn-primary relative overflow-hidden rounded-md px-6 py-2.5 text-sm font-medium text-white shadow-md transition-colors duration-300 hover:bg-primary-500 active:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
                          >
                            <ImCogs className="mr-2 text-lg" />
                            Assign Provider
                          </Button>
                        )}

                        <div ref={dropdownRef} className="relative ml-2">
                          <Button
                            onClick={toggleDropdown}
                            isDisabled={
                              batchDetailForEdit === null ||
                              isLoading ||
                              isDownloading ||
                              isFetchProvider
                            }
                            className="
                              bg-transparent
                              relative flex h-9 w-9 items-center
                              justify-center
                              rounded-full
                              text-gray-500
                              transition
                              hover:bg-gray-100 hover:text-gray-700
                              active:bg-gray-200
                              disabled:cursor-not-allowed disabled:opacity-40
                              dark:text-gray-400
                              dark:hover:bg-white/10 dark:hover:text-white
                            "
                            aria-label="More actions"
                          >
                            <HiDotsVertical className="h-5 w-5" />
                          </Button>

                          {isDropdownOpen && (
                            <div className="absolute right-0 mt-2 w-48 origin-top-right rounded-md border border-gray-300 bg-white shadow-lg">
                              <div
                                className="p-2"
                                role="menu"
                                aria-orientation="vertical"
                              >
                                {(batchDetailForEdit.status === "open" ||
                                  batchDetailForEdit.status === "close" ||
                                  batchDetailForEdit.status === "queue") && (
                                  <span
                                    onClick={() => handleClickCancel()}
                                    className="flex cursor-pointer items-center rounded-md px-2 py-2 text-sm font-medium text-red-500 hover:bg-red-100"
                                  >
                                    <MdOutlineCancel className="mr-2 h-4 w-4 shrink-0" />
                                    Cancel
                                  </span>
                                )}

                                {batchDetailForEdit.status === "in_progress" &&
                                  batchDetailForEdit.is_manual === "true" && (
                                    <span
                                      onClick={() =>
                                        handleClickUpdateStatus("failed")
                                      }
                                      className="flex cursor-pointer items-center rounded-md px-2 py-2 text-sm font-medium text-red-500 hover:bg-red-100 dark:hover:bg-red-500/10"
                                    >
                                      <MdOutlineCancel className="mr-2 h-4 w-4 shrink-0" />
                                      Mark Batch as Failed
                                    </span>
                                  )}

                                {(batchDetailForEdit.status === "open" ||
                                  batchDetailForEdit.status === "close") &&
                                  batchDetailForEdit.status !== "open" && (
                                    <span
                                      onClick={() =>
                                        handleClickAssignProvider()
                                      }
                                      className="flex cursor-pointer items-center rounded-md px-2 py-2 text-sm font-medium text-navy-500 hover:bg-navy-50"
                                    >
                                      <ImCogs className="mr-2 text-lg" />
                                      Assign Provider
                                    </span>
                                  )}
                              </div>
                            </div>
                          )}
                        </div>
                      </>
                    )}
                </div>
              </div>
            </div>
            <hr className="mb-8" />
            <div className="text-sm text-navy-700 dark:text-white">
              <div className="grid grid-cols-1 gap-x-6 gap-y-4 pb-4 md:grid-cols-2">
                <div>
                  Created Date:{" "}
                  <span className="ml-1 font-semibold">
                    {batchDetailForEdit?.created_at
                      ? formatDate(batchDetailForEdit.created_at)
                      : "-"}
                  </span>
                </div>

                <div>
                  Process Date:{" "}
                  <span className="ml-1 font-semibold">
                    {batchDetailForEdit?.processed_at
                      ? formatDate(batchDetailForEdit.processed_at, true)
                      : "-"}
                  </span>
                </div>

                {userRole === "superadmin" && batchDetailForEdit?.merchant && (
                  <>
                    <div>
                      Merchant:{" "}
                      <span className="ml-1 font-semibold">
                        {batchDetailForEdit?.merchant?.name || "-"}
                      </span>
                    </div>
                    <div>
                      Subaccount:{" "}
                      <span className="ml-1 font-semibold">
                        {batchDetailForEdit?.subaccount?.name || "-"}
                      </span>
                    </div>
                  </>
                )}

                <div>
                  Reference:{" "}
                  <span className="ml-1 font-semibold">
                    {batchDetailForEdit?.batch_reference || "-"}
                  </span>
                </div>

                <div>
                  Total Record(s):{" "}
                  <span className="ml-1 font-semibold">
                    {batchDetailForEdit?.record || "-"}
                  </span>
                </div>

                <div>
                  Processed Record(s):{" "}
                  <span className="ml-1 font-semibold">
                    {batchDetailForEdit?.total_tx &&
                    batchDetailForEdit?.status !== "queue"
                      ? batchDetailForEdit.total_tx
                      : "-"}
                  </span>
                </div>

                {userRole === "superadmin" &&
                  batchDetailForEdit?.file_bucket &&
                  batchDetailForEdit?.file_key && (
                    <div className="flex items-center gap-2">
                      File:{" "}
                      <span className="ml-1 font-semibold">
                        {batchDetailForEdit.file_name}
                      </span>
                      <Button
                        isDisabled={isDownloading}
                        onClick={() =>
                          handleClickDownload(
                            batchDetailForEdit.file_bucket,
                            batchDetailForEdit.file_key,
                            batchDetailForEdit.file_name
                          )
                        }
                        className="ml-1 flex items-center gap-1 rounded-lg bg-cyan-500 px-2 py-1 text-xs text-white hover:bg-cyan-600 active:bg-cyan-700 dark:bg-cyan-400 dark:hover:bg-cyan-300"
                      >
                        Download <AiOutlineDownload className="h-4 w-4" />
                      </Button>
                    </div>
                  )}

                <div>
                  Total Amount:{" "}
                  <span className="ml-1 font-semibold">
                    {batchDetailForEdit?.amount ? (
                      batchDetailForEdit.crypto ? (
                        batchDetailForEdit.amount
                      ) : (
                        <DisplayAmount
                          amount={Number(batchDetailForEdit.amount)}
                          currency={batchDetailForEdit.asset}
                        />
                      )
                    ) : (
                      "-"
                    )}
                  </span>
                </div>

                <div>
                  Asset:{" "}
                  <span className="ml-1 font-semibold">
                    {batchDetailForEdit?.asset || "-"}
                  </span>
                </div>

                <div>
                  Status:{" "}
                  <span className="ml-1 font-semibold">
                    {batchDetailForEdit?.status
                      ? statusDisplay(batchDetailForEdit.status)
                      : "-"}
                  </span>
                </div>

                {userRole === "superadmin" &&
                  batchDetailForEdit?.provider_name &&
                  batchDetailForEdit?.provider_account_name &&
                  batchDetailForEdit?.provider_method_name && (
                    <>
                      <div>
                        Provider:{" "}
                        <span className="ml-1 font-semibold">
                          {`${batchDetailForEdit.provider_name} (${batchDetailForEdit.provider_account_name})`}
                        </span>
                      </div>
                      <div>
                        Provider Method:{" "}
                        <span className="ml-1 font-semibold">
                          {batchDetailForEdit.provider_method_name}
                        </span>
                      </div>
                    </>
                  )}

                {batchDetailForEdit?.validation_results?.some(
                  (x: any) => !x.isValid
                ) && (
                  <div>
                    Error Info:{" "}
                    <Button
                      isDisabled={!batchDetailForEdit}
                      onClick={showErrorInfoModal}
                      className="btn btn-primary"
                    >
                      View
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </>
        ) : (
          <>
            <div
              className="mb-3 mt-3 h-5 animate-pulse rounded-md bg-gray-200"
              style={{ animationDuration: "0.6s" }}
            ></div>
            <div
              className="mb-3 h-5 animate-pulse rounded-md bg-gray-200"
              style={{ animationDuration: "0.6s" }}
            ></div>
            <div
              className="mb-3 h-5 animate-pulse rounded-md bg-gray-200"
              style={{ animationDuration: "0.6s" }}
            ></div>
          </>
        )}
      </Card>

      {batchDetailForEdit &&
        (batchDetailForEdit.status !== "queue" ||
          batchDetailForEdit.batch_type === "API") && (
          <Card className="card mt-3">
            <BatchPayoutList
              userPermissions={userPermissions}
              isLoading={isLoading}
              page={page}
              pageSize={pageSize}
              rowCount={rowCount}
              tableData={isLoading ? [] : tableData}
              setPaginationModel={setPaginationModel}
              currentLastKey={currentLastKey}
              triggerReloadPayoutList={triggerReloadPayoutList}
              batchType={batchDetailForEdit.batch_type}
              batchFileBucket={batchDetailForEdit.file_bucket}
              batchFileKey={batchDetailForEdit.file_key}
              batchStatus={batchDetailForEdit.status}
            />
          </Card>
        )}

      <SweetAlert2
        {...swalProps}
        customClass={{
          actions: "custom-swal2-popup-action",
          title: "custom-swal2-popup-title",
          confirmButton:
            "custom-swal2-popup-action custom-swal2-popup-action-confirm",
          cancelButton:
            "custom-swal2-popup-action custom-swal2-popup-action-cancel",
        }}
      ></SweetAlert2>
    </>
  );
};

export default BatchDetail;
